import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { AlertTriangle, Check, Link2, Search, X } from "react-feather";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { formatAmount, formatMoney } from "../../../utils/formatMoney";
import { can, formatShortDate, PARTY_TYPES, partyTypeLabel, PERMISSIONS } from "./glUtils";
import { TABLE_STYLES, useStickyTableHeader } from "./tableLayout";

const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
  menu: (base) => ({ ...base, fontSize: "0.85rem", minWidth: 200 }),
};

const toOptions = (values) => (values || []).map((v) => ({ value: String(v), label: String(v) }));

function PartyBadges({ party }) {
  const type = party.party_type || "unclassified";
  return (
    <span className="gl-party-badges">
      <span className={`gl-party-type gl-party-${PARTY_TYPES[type]?.tone || "other"}`}>{partyTypeLabel(type)}</span>
      {party.provider && <span className="gl-provider">{party.provider}</span>}
      {party.linked && (
        <span className="gl-linked" title={`Linked to ${partyTypeLabel(party.linked.type).toLowerCase()} ${party.linked.name}`}>
          <Link2 size={11} /> linked
        </span>
      )}
      {party.source === "auto" && (
        <span className="gl-auto" title={party.matched ? `Detected automatically (matched “${party.matched}”)` : "Detected automatically"}>
          auto
        </span>
      )}
    </span>
  );
}

/** One line per currency: amounts in different currencies are never added together. */
function AmountLines({ rows, field, className }) {
  const lines = (rows || []).filter((r) => Number(r[field]) > 0);
  if (lines.length === 0) return <span className="gl-muted">—</span>;
  return (
    <div className="gl-amount-lines">
      {lines.map((r) => (
        <div key={r.currency} className={`gl-amount ${className}`}>
          {formatAmount(r[field], r.currency)} <span className="gl-muted">{r.currency}</span>
        </div>
      ))}
    </div>
  );
}

const REVIEW_REASONS = {
  processor: "Paid through the same payment processor",
  same_name: "Same name once numbers, punctuation, titles and legal forms (SARL, LTD…) are ignored",
  truncated: "The bank cut the name short",
};

const MEMBER_STATUS = {
  auto: { label: "Grouped automatically", tone: "gl-review" },
  confirmed: { label: "Confirmed", tone: "gl-confirmed" },
  single: { label: "", tone: "" },
};

function GroupBadges({ party, onOpen }) {
  const names = party.members?.length || 0;
  if (!party.needs_review && names < 2) return null;
  return (
    <>
      {names > 1 && (
        <button type="button" className="gl-names-badge" onClick={onOpen} title="Bank names in this group">
          {names} names
        </button>
      )}
      {party.needs_review && (
        <button type="button" className="gl-review-flag" onClick={onOpen} title="Check that these bank names are really the same party">
          <AlertTriangle size={11} /> Review
        </button>
      )}
    </>
  );
}

function GroupReviewModal({ party, canReview, onClose }) {
  const queryClient = useQueryClient();
  const [split, setSplit] = useState([]);
  const members = party.members || [];
  const autoMembers = members.filter((m) => m.status === "auto");

  const mutation = useMutation({
    mutationFn: (payload) => GeneralLedgerService.reviewCounterpartyGroup({ group_key: party.key, ...payload }),
    onSuccess: (_, payload) => {
      const messages = {
        confirm: "Group confirmed",
        split: "Names moved out of the group",
        merge: "Parties merged",
        not_same: "Marked as different parties",
        reset: "Back to automatic grouping",
      };
      toast.success(messages[payload.action]);
      queryClient.invalidateQueries({ queryKey: ["glCounterparties"] });
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not save the review.")),
  });

  const toggleSplit = (key) => setSplit((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));
  const busy = mutation.isPending;
  const reviewed = members.some((m) => m.status === "confirmed");

  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Bank names grouped as “{party.display_name}”</ModalHeader>
      <ModalBody>
        {party.review_reasons?.length > 0 && (
          <div className="alert alert-warning py-2 small">
            <strong>Please check:</strong> these names were grouped automatically —{" "}
            {party.review_reasons.map((r) => REVIEW_REASONS[r] || r).join("; ")}.
            {canReview && " Confirm if they are all the same party, or untick the ones that are not."}
          </div>
        )}

        <table className="table table-sm gl-members-table mb-3">
          <thead>
            <tr>
              {canReview && members.length > 1 && <th style={{ width: 90 }}>Same party</th>}
              <th>Name printed by the bank</th>
              <th className="text-end">Moves</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.key} className={split.includes(m.key) ? "gl-member-split" : ""}>
                {canReview && members.length > 1 && (
                  <td>
                    <Input
                      type="checkbox"
                      aria-label={`${m.name} is the same party`}
                      checked={!split.includes(m.key)}
                      disabled={busy}
                      onChange={() => toggleSplit(m.key)}
                    />
                  </td>
                )}
                <td className="gl-cell-strong">{m.name}</td>
                <td className="text-end">{m.entries}</td>
                <td>
                  {MEMBER_STATUS[m.status]?.label && (
                    <span className={`gl-member-status ${MEMBER_STATUS[m.status].tone}`} title={REVIEW_REASONS[m.reason] || ""}>
                      {MEMBER_STATUS[m.status].label}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {party.suggestions?.length > 0 && (
          <>
            <h6 className="mb-2">Possibly the same party</h6>
            <p className="gl-muted small mb-2">These names look alike but were not grouped because we are not sure.</p>
            <ul className="list-unstyled gl-suggestions mb-0">
              {party.suggestions.map((s) => (
                <li key={s.key}>
                  <span className="gl-cell-strong">{s.name}</span>
                  <span className="gl-muted small ms-2">{s.score}% similar</span>
                  {canReview && (
                    <span className="ms-auto d-flex gap-1">
                      <Button size="sm" color="primary" outline className="btn-xs" disabled={busy}
                        onClick={() => mutation.mutate({ action: "merge", keys: [s.key] })}>
                        Same party — merge
                      </Button>
                      <Button size="sm" color="light" className="btn-xs" disabled={busy}
                        onClick={() => mutation.mutate({ action: "not_same", keys: [s.key] })}>
                        Different
                      </Button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-between">
        {canReview ? (
          <Button color="link" className="text-danger px-0" disabled={busy || !reviewed} onClick={() => mutation.mutate({ action: "reset" })}>
            Reset to automatic grouping
          </Button>
        ) : (
          <span className="gl-muted small">You can view this group but not change it.</span>
        )}
        <div className="d-flex gap-2">
          <Button color="secondary" onClick={onClose}>Close</Button>
          {canReview && members.length > 1 && (
            split.length > 0 ? (
              <Button color="primary" disabled={busy || split.length === members.length}
                onClick={() => mutation.mutate({ action: "split", keys: split })}>
                Move {split.length} name{split.length > 1 ? "s" : ""} out of the group
              </Button>
            ) : (
              <Button color="primary" disabled={busy || (autoMembers.length === 0 && !party.review_reasons?.length)}
                onClick={() => mutation.mutate({ action: "confirm" })}>
                Confirm — all the same party
              </Button>
            )
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
}

const LINKABLE_TYPES = ["artist", "label", "contributor", "staff"];

const recordOption = (record) => record && { value: `${record.type}:${record.id}`, label: record.name, record };

const formatRecordOption = ({ record }) => (
  <div className="gl-record-option">
    <span>{record.name}</span>
    {record.subtitle && <small className="gl-muted">{record.subtitle}</small>}
  </div>
);

/** "Is this who you are looking for?" — best record matching the party's bank names, answered with Yes / No. */
function MatchSuggestion({ suggestion, busy, onYes, onNo }) {
  return (
    <div className="gl-match-card">
      <div className="gl-match-question">Is this who you are looking for?</div>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className={`gl-party-type gl-party-${PARTY_TYPES[suggestion.type]?.tone || "other"}`}>{partyTypeLabel(suggestion.type)}</span>
        <strong>{suggestion.name}</strong>
        {suggestion.subtitle && <span className="gl-muted small">{suggestion.subtitle}</span>}
        <span className="gl-match-score" title="How close the names are">{suggestion.score}%</span>
      </div>
      <div className="gl-muted small mt-1">{suggestion.reason}</div>
      <div className="d-flex gap-2 mt-2">
        <Button size="sm" color="primary" disabled={busy} onClick={onYes}>
          <Check size={13} /> Yes, link it
        </Button>
        <Button size="sm" color="light" disabled={busy} onClick={onNo}>
          No, not this one
        </Button>
      </div>
    </div>
  );
}

function ClassifyModal({ party, meta, onClose }) {
  const queryClient = useQueryClient();
  const autoName = party.linked && party.display_name === party.linked.name;
  const [form, setForm] = useState({
    display_name: party.display_name !== party.name && !autoName ? party.display_name : "",
    party_type: party.party_type || "",
    provider: party.provider || "",
  });
  const [linked, setLinked] = useState(party.linked || null);
  const [dismissed, setDismissed] = useState([]);
  const linkable = LINKABLE_TYPES.includes(form.party_type);

  const setType = (partyType) => {
    setForm((current) => ({ ...current, party_type: partyType }));
    if (linked && linked.type !== partyType) setLinked(null);
  };

  // Without a type yet, look in every kind of record; the answer also sets the type.
  const matchType = linkable ? form.party_type : form.party_type ? null : "";
  const matchesQuery = useQuery({
    queryKey: ["glPartyMatches", party.key, matchType],
    queryFn: () => GeneralLedgerService.getPartyMatches({ key: party.key, ...(matchType && { type: matchType }) }),
    enabled: matchType !== null,
    staleTime: 60_000,
  });
  const suggestion = (matchesQuery.data?.data || []).find(
    (s) => !dismissed.includes(`${s.type}:${s.id}`) && !(linked && linked.type === s.type && linked.id === s.id)
  );

  const reject = useMutation({
    mutationFn: (s) => GeneralLedgerService.rejectPartyMatch({ key: party.key, type: s.type, id: s.id }),
    onMutate: (s) => setDismissed((current) => [...current, `${s.type}:${s.id}`]),
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not save your answer.")),
  });

  const acceptSuggestion = (s) => {
    setLinked(s);
    setForm((current) => ({ ...current, party_type: s.type }));
  };

  const loadRecords = useMemo(() => {
    let timer;
    return (input) =>
      new Promise((resolve) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(async () => {
          try {
            const response = await GeneralLedgerService.searchPartyRecords({ type: form.party_type, q: input });
            resolve((response?.data || []).map(recordOption));
          } catch {
            resolve([]);
          }
        }, 250);
      });
  }, [form.party_type]);

  const save = (payload) =>
    GeneralLedgerService.updateCounterparty({ name: party.key, ...payload });

  const mutation = useMutation({
    mutationFn: save,
    onSuccess: (_, payload) => {
      toast.success(payload.party_type || payload.provider || payload.display_name ? "Classification saved" : "Classification cleared");
      queryClient.invalidateQueries({ queryKey: ["glCounterparties"] });
      queryClient.invalidateQueries({ queryKey: ["glPartyMatches"] });
      queryClient.invalidateQueries({ queryKey: ["glMeta"] });
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not save the classification.")),
  });

  const providerOptions = toOptions(meta?.providers);
  const providerValue = form.provider ? { value: form.provider, label: form.provider } : null;
  const typeLabel = partyTypeLabel(form.party_type).toLowerCase();

  return (
    <Modal isOpen toggle={onClose}>
      <ModalHeader toggle={onClose}>Classify “{party.name}”</ModalHeader>
      <ModalBody>
        <p className="gl-muted mb-3">
          {party.members?.length > 1
            ? `Applies to every bank movement of the ${party.members.length} names in this group, in all companies.`
            : "Applies to every bank movement with this name, in all companies."}
          {party.source === "auto" && " The current values were detected automatically."}
        </p>
        {suggestion && (
          <MatchSuggestion
            suggestion={suggestion}
            busy={reject.isPending || mutation.isPending}
            onYes={() => acceptSuggestion(suggestion)}
            onNo={() => reject.mutate(suggestion)}
          />
        )}
        <div className="mb-3">
          <Label>Type</Label>
          <Input type="select" value={form.party_type} onChange={(e) => setType(e.target.value)}>
            <option value="">Unclassified</option>
            {(meta?.party_types || []).map((t) => (
              <option key={t} value={t}>{partyTypeLabel(t)}</option>
            ))}
          </Input>
        </div>
        {linkable && (
          <div className="mb-3">
            <Label>Linked {typeLabel}</Label>
            <AsyncSelect
              key={form.party_type}
              isClearable
              cacheOptions
              defaultOptions
              classNamePrefix="gl-select"
              menuPortalTarget={document.body}
              styles={SELECT_STYLES}
              placeholder={`Search ${typeLabel}s by name…`}
              loadOptions={loadRecords}
              value={recordOption(linked)}
              formatOptionLabel={formatRecordOption}
              noOptionsMessage={({ inputValue }) => (inputValue ? `No ${typeLabel} named “${inputValue}”` : `No ${typeLabel}s found`)}
              onChange={(option) => setLinked(option?.record || null)}
            />
            <small className="gl-muted">
              {linked
                ? `Every movement of this party will be attributed to ${linked.name}, including future statements.`
                : `Link this party to a ${typeLabel} of the app so it is recognized automatically next time.`}
            </small>
          </div>
        )}
        <div className="mb-3">
          <Label>Provider</Label>
          <CreatableSelect
            isClearable
            classNamePrefix="gl-select"
            menuPortalTarget={document.body}
            styles={SELECT_STYLES}
            placeholder="e.g. YouTube, FUGA…"
            options={providerOptions}
            value={providerValue}
            onChange={(option) => setForm({ ...form, provider: option?.value || "" })}
            formatCreateLabel={(value) => `Add provider “${value}”`}
          />
        </div>
        <div>
          <Label>Display name</Label>
          <Input
            value={form.display_name}
            maxLength={191}
            placeholder={linked?.name || party.name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <Button
          color="link"
          className="text-danger px-0"
          disabled={mutation.isPending || party.source !== "manual"}
          onClick={() => mutation.mutate({ display_name: null, party_type: null, provider: null, linked_type: null })}
        >
          Reset to automatic
        </Button>
        <div className="d-flex gap-2">
          <Button color="secondary" onClick={onClose}>Cancel</Button>
          <Button
            color="primary"
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                display_name: form.display_name.trim() || null,
                party_type: form.party_type || null,
                provider: form.provider.trim() || null,
                linked_type: linkable && linked ? linked.type : null,
                linked_id: linkable && linked ? linked.id : null,
              })
            }
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

const EMPTY_FILTERS = { q: "", party_type: "", needs_review: false, provider: [], company_id: [], date_from: "", date_to: "" };

export default function PartiesTab({ meta, onOpenLedger }) {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ field: "last_date", direction: "desc" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [classifying, setClassifying] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [tableRef, stickyStyle] = useStickyTableHeader();
  const canClassify = can(meta, PERMISSIONS.ANNOTATE);

  const update = (patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (filters.q !== search.trim()) update({ q: search.trim() });
    }, 400);
    return () => window.clearTimeout(handle);
  }, [search]);

  const params = {
    page,
    per_page: perPage,
    sort: sort.field,
    direction: sort.direction,
    ...(filters.q && { q: filters.q }),
    ...(filters.party_type && { party_type: filters.party_type }),
    ...(filters.needs_review && { needs_review: 1 }),
    ...(filters.provider.length && { provider: filters.provider.join(",") }),
    ...(filters.company_id.length && { company_id: filters.company_id.join(",") }),
    ...(filters.date_from && { date_from: filters.date_from }),
    ...(filters.date_to && { date_to: filters.date_to }),
  };

  const query = useQuery({
    queryKey: ["glCounterparties", params],
    queryFn: () => GeneralLedgerService.getCounterparties(params),
    placeholderData: (previous) => previous,
  });

  const summary = query.data?.summary;
  const byType = summary?.by_type || {};
  const totalParties = Object.values(byType).reduce((sum, n) => sum + n, 0);
  const sortCurrency = query.data?.meta?.sort_currency;
  const hasFilters = Boolean(
    filters.q || filters.party_type || filters.needs_review || filters.provider.length || filters.company_id.length || filters.date_from || filters.date_to
  );

  const companyOptions = useMemo(
    () => (meta?.companies || []).map((c) => ({ value: String(c.id), label: c.name })),
    [meta]
  );
  const providerOptions = useMemo(() => toOptions(meta?.providers), [meta]);

  const ledgerFor = (party) =>
    onOpenLedger({
      ...(party.ledger_filter || { party: party.key }),
      party_name: party.display_name,
      ...(filters.company_id.length && { company_id: filters.company_id.join(",") }),
      ...(filters.date_from && { date_from: filters.date_from }),
      ...(filters.date_to && { date_to: filters.date_to }),
    });

  const columns = [
    {
      id: "name",
      name: "Party",
      sortable: true,
      grow: 3,
      minWidth: "220px",
      cell: (p) => (
        <div className="gl-desc">
          <div className="gl-cell-strong" title={(p.members || []).map((m) => m.name).join("\n") || p.name}>{p.display_name}</div>
          <span className="gl-party-badges">
            <PartyBadges party={p} />
            <GroupBadges party={p} onOpen={() => setReviewing(p)} />
          </span>
        </div>
      ),
    },
    {
      id: "first_date",
      name: "Since",
      sortable: true,
      width: "112px",
      cell: (p) => <span className="gl-date" title={p.first_date}>{formatShortDate(p.first_date)}</span>,
    },
    {
      id: "last_paid_date",
      name: "Last paid",
      sortable: true,
      width: "112px",
      cell: (p) =>
        p.last_paid_date ? <span className="gl-date" title={p.last_paid_date}>{formatShortDate(p.last_paid_date)}</span> : <span className="gl-muted">—</span>,
    },
    {
      id: "last_received_date",
      name: "Last received",
      sortable: true,
      width: "120px",
      hide: "md",
      cell: (p) =>
        p.last_received_date ? <span className="gl-date" title={p.last_received_date}>{formatShortDate(p.last_received_date)}</span> : <span className="gl-muted">—</span>,
    },
    {
      id: "entries",
      name: "Moves",
      sortable: true,
      width: "80px",
      right: true,
      hide: "sm",
      cell: (p) => p.entries,
    },
    {
      id: "paid",
      name: "Total paid",
      sortable: true,
      right: true,
      minWidth: "120px",
      maxWidth: "170px",
      cell: (p) => <AmountLines rows={p.by_currency} field="paid" className="text-danger" />,
    },
    {
      id: "received",
      name: "Total received",
      sortable: true,
      right: true,
      minWidth: "120px",
      maxWidth: "170px",
      cell: (p) => <AmountLines rows={p.by_currency} field="received" className="text-success" />,
    },
    {
      id: "actions",
      name: "",
      width: canClassify ? "150px" : "80px",
      cell: (p) => (
        <div className="d-flex gap-1">
          <Button size="sm" color="light" className="btn-xs" onClick={() => ledgerFor(p)}>Ledger</Button>
          {canClassify && (
            <Button size="sm" color="light" className="btn-xs" onClick={() => setClassifying(p)}>Classify</Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="gl-filter-bar">
        <div className="gl-search">
          <Search size={15} />
          <Input
            type="search"
            placeholder="Search name or provider…"
            value={search}
            maxLength={100}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="gl-date-range">
          <Input
            type="date"
            aria-label="From"
            title="From date"
            className={filters.date_from ? "" : "gl-date-empty"}
            value={filters.date_from}
            onChange={(e) => update({ date_from: e.target.value })}
          />
          <span className="gl-muted">→</span>
          <Input
            type="date"
            aria-label="To"
            title="To date"
            className={filters.date_to ? "" : "gl-date-empty"}
            value={filters.date_to}
            onChange={(e) => update({ date_to: e.target.value })}
          />
        </div>
        <div className="gl-filter-select">
          <Select
            isMulti
            classNamePrefix="gl-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={SELECT_STYLES}
            placeholder="All companies"
            options={companyOptions}
            value={companyOptions.filter((o) => filters.company_id.includes(o.value))}
            onChange={(selected) => update({ company_id: (selected || []).map((s) => s.value) })}
          />
        </div>
        <div className="gl-filter-select">
          <Select
            isMulti
            classNamePrefix="gl-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={SELECT_STYLES}
            placeholder="All providers"
            options={providerOptions}
            value={providerOptions.filter((o) => filters.provider.includes(o.value))}
            onChange={(selected) => update({ provider: (selected || []).map((s) => s.value) })}
          />
        </div>
        {hasFilters && (
          <div className="gl-filter-actions">
            <Button
              color="link"
              className="gl-reset d-inline-flex align-items-center gap-1"
              onClick={() => {
                setSearch("");
                setFilters(EMPTY_FILTERS);
                setPage(1);
              }}
            >
              <X size={14} /> Clear
            </Button>
          </div>
        )}
      </div>

      <div className="gl-type-chips">
        <button type="button" className={`gl-chip ${filters.party_type === "" ? "active" : ""}`} onClick={() => update({ party_type: "" })}>
          All <span>{totalParties}</span>
        </button>
        {Object.keys(PARTY_TYPES)
          .filter((type) => byType[type])
          .map((type) => (
            <button
              key={type}
              type="button"
              className={`gl-chip gl-chip-${PARTY_TYPES[type].tone} ${filters.party_type === type ? "active" : ""}`}
              onClick={() => update({ party_type: filters.party_type === type ? "" : type })}
            >
              {PARTY_TYPES[type].plural} <span>{byType[type]}</span>
            </button>
          ))}
        {(summary?.needs_review > 0 || filters.needs_review) && (
          <button
            type="button"
            className={`gl-chip gl-chip-review ms-auto ${filters.needs_review ? "active" : ""}`}
            title="Groups made automatically, or look-alike names, waiting for an admin to confirm"
            onClick={() => update({ needs_review: !filters.needs_review })}
          >
            <AlertTriangle size={12} /> Needs review <span>{summary?.needs_review || 0}</span>
          </button>
        )}
      </div>

      {summary?.by_currency?.length > 0 && (
        <div className="gl-totals-strip mb-2">
          {summary.by_currency.map((t) => (
            <div key={t.currency} className="gl-total-item">
              <div className="gl-total-head">
                <span className="gl-total-currency">{t.currency}</span>
                <span className="gl-total-count">{t.parties} parties</span>
              </div>
              <div className="gl-total-values">
                <div>
                  <small>Paid out</small>
                  <strong className="text-danger">{formatMoney(t.paid, t.currency)}</strong>
                </div>
                <div>
                  <small>Received</small>
                  <strong className="text-success">{formatMoney(t.received, t.currency)}</strong>
                </div>
                <div>
                  <small>Net</small>
                  <strong className={Number(t.net) < 0 ? "text-danger" : "text-success"}>{formatMoney(t.net, t.currency)}</strong>
                </div>
              </div>
            </div>
          ))}
          {sortCurrency && ["paid", "received"].includes(sort.field) && summary.by_currency.length > 1 && (
            <span className="gl-muted">Sorted by {sortCurrency} totals</span>
          )}
        </div>
      )}

      {query.isError && (
        <div className="alert alert-danger py-2">{getApiErrorMessage(query.error, "Could not load the parties.")}</div>
      )}

      <div ref={tableRef} style={stickyStyle}>
        <DataTable
          className="gl-table"
          columns={columns}
          customStyles={TABLE_STYLES}
          data={query.data?.data || []}
          keyField="key"
          progressPending={query.isLoading}
          pagination
          paginationServer
          paginationTotalRows={query.data?.meta?.total || 0}
          paginationDefaultPage={page}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[25, 50, 100, 200]}
          onChangePage={setPage}
          onChangeRowsPerPage={(size) => {
            setPerPage(size);
            setPage(1);
          }}
          sortServer
          defaultSortFieldId="last_date"
          defaultSortAsc={false}
          onSort={(column, direction) => {
            setSort({ field: column.id, direction });
            setPage(1);
          }}
          highlightOnHover
          dense
          responsive={false}
          noDataComponent={
            <div className="p-4 gl-muted">
              No parties found. Parties come from the counterparty name the bank prints on transfers and card payments;
              bank fees, VAT and cash withdrawals are not counted.
            </div>
          }
        />
      </div>

      {classifying && <ClassifyModal party={classifying} meta={meta} onClose={() => setClassifying(null)} />}
      {reviewing && <GroupReviewModal party={reviewing} canReview={canClassify} onClose={() => setReviewing(null)} />}
    </div>
  );
}
